import { Field, Form, Formik, FormikErrors } from "formik";
import moment from "moment";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import dateService, { API_DATE_FORMAT } from "../../../services/date.service";

const scheduleSessionVisitTypeSchema = Yup.object({
  visitType: Yup.string().required("Please select a visit type"),
});

interface scheduleSessionVisitTypeProps {
  value: any;
  setCurrentOverlay: React.Dispatch<React.SetStateAction<string>>;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<{
    visitType: string;
    location: string;
    preferredDate: string;
    specialty: string;
    visitReason: string;
  }>>;
}

const ScheduleSessionPreferredDate: React.FC<scheduleSessionVisitTypeProps> = ({
  value,
  setCurrentOverlay,
  setFieldValue,
}) => {
  const [selectedMonth, setSelectedMonth] = useState<string>();
  // [selectedDate, setSelectedDate] = useState<string>();

  const moveLeft = () => {
    const momentObj = dateService
      .convertDateStrToMoment(selectedMonth, API_DATE_FORMAT)
      .subtract(1, "months");
    setSelectedMonth(dateService.getStartOfMonth(momentObj));
  };

  const moveRight = () => {
    const momentObj = dateService
      .convertDateStrToMoment(selectedMonth, API_DATE_FORMAT)
      .add(1, "months");
    setSelectedMonth(dateService.getStartOfMonth(momentObj));
  };

  useEffect(() => {
    const momentObj = dateService.getMomentDate();
    setSelectedMonth(dateService.getStartOfMonth(momentObj));
  }, []);

  const onDateSelect = (date: string, currentValue: string, setFieldValue) => {
    if (date === currentValue) {
      return;
    }
    setFieldValue("date", date);
  };

  const renderMonthDates = (selectedMonth: string, values, setFieldValue) => {
    const momentObj = dateService.convertDateStrToMoment(
        selectedMonth,
        API_DATE_FORMAT
      ),
      monthDates: string[] = [],
      month = momentObj.format("MM"),
      year = momentObj.format("YYYY"),
      daysInMonth = momentObj.daysInMonth();

    // populate empty weekdays at month start
    for (let i = 0; i < momentObj.day(); i++) {
      monthDates.push("");
    }

    // populate month dates
    for (let i = 1; i <= daysInMonth; i++) {
      monthDates.push(momentObj.format("DD"));
      momentObj.add(1, "days");
    }

    return (
      <div className="grid grid-cols-7 gap-4 mt-6 md:gap-6 place-items-center ">
        <p className="text-xs font-semibold text-primary uppercase">S</p>
        <p className="text-xs font-semibold text-primary uppercase">M</p>
        <p className="text-xs font-semibold text-primary uppercase">T</p>
        <p className="text-xs font-semibold text-primary uppercase">W</p>
        <p className="text-xs font-semibold text-primary uppercase">T</p>
        <p className="text-xs font-semibold text-primary uppercase">F</p>
        <p className="text-xs font-semibold text-primary uppercase">S</p>
        {monthDates.map((date, index) => {
          const monthDate = `${year}-${month}-${date}`;

          return !date ? (
            <span key={index}></span>
          ) : (
            <button
              key={index}
              type="button"
              onClick={() =>
                onDateSelect(monthDate, values.date, setFieldValue)
              }
              disabled={dateService.isDateInPast(monthDate)}
              className={`flex items-center justify-center w-8 h-8 text-base text-gray-800 transition-colors duration-300 rounded-full md:text-lg hover:text-white hover:bg-primary focus:outline-none ${
                monthDate === values.date ? "bg-primary text-white" : ""
              } disabled:opacity-40 disabled:pointer-events-none`}>
              {date}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="mt-4 relative h-full">
      {/* Heading */}
      <p className="items-center justify-center gap-3 text-primary hidden max-lg:flex">
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M11.75 1.75H14.75C14.9489 1.75 15.1397 1.82902 15.2803 1.96967C15.421 2.11032 15.5 2.30109 15.5 2.5V14.5C15.5 14.6989 15.421 14.8897 15.2803 15.0303C15.1397 15.171 14.9489 15.25 14.75 15.25H1.25C1.05109 15.25 0.860322 15.171 0.71967 15.0303C0.579018 14.8897 0.5 14.6989 0.5 14.5V2.5C0.5 2.30109 0.579018 2.11032 0.71967 1.96967C0.860322 1.82902 1.05109 1.75 1.25 1.75H4.25V0.25H5.75V1.75H10.25V0.25H11.75V1.75ZM14 7.75H2V13.75H14V7.75ZM10.25 3.25H5.75V4.75H4.25V3.25H2V6.25H14V3.25H11.75V4.75H10.25V3.25ZM3.5 9.25H5V10.75H3.5V9.25ZM7.25 9.25H8.75V10.75H7.25V9.25ZM11 9.25H12.5V10.75H11V9.25Z"
            fill="#3537E8"
          />
        </svg>
        Preferred Date
      </p>
      <p className="max-lg:text-center font-semibold mt-5 mx-2 mb-6 pl-12 max-lg:pl-0">
        When would you like to see a provider?
      </p>

      <Formik
        initialValues={{
          date: moment().format(API_DATE_FORMAT),
          nextAvailable: value.preferredDate === "Next Available",
        }}
        onSubmit={(values) => {
          if (values.nextAvailable)
            setFieldValue("preferredDate", "Next Available");
          else setFieldValue("preferredDate", `${values.date}`);
          setCurrentOverlay("");
        }}>
        {({ values, setFieldValue }) => (
          <Form>
            <div className="w-2/5 max-lg:w-full h-full max-lg:block max-lg:h-auto">
              <div className="mt-5 pl-12 max-lg:pl-0">
                <div className="flex items-center justify-between w-full mx-auto text-lg text-gray-800 focus:outline-none">
                  <button
                    type="button"
                    onClick={moveLeft}
                    className="focus:outline-none">
                    <svg
                      width="16"
                      height="13"
                      viewBox="0 0 16 13"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M16 6.49983C16 6.14357 15.7311 5.85461 15.4001 5.85461H2.04836L6.46786 1.10143C6.70223 0.849364 6.70223 0.440879 6.46786 0.189047C6.23391 -0.0630156 5.85347 -0.0630156 5.61953 0.189047L0.175776 6.04381C-0.0585919 6.29587 -0.0585919 6.70436 0.175776 6.95619L5.61953 12.811C5.7365 12.9372 5.8901 13 6.04369 13C6.19729 13 6.35088 12.937 6.46786 12.811C6.70223 12.5589 6.70223 12.1504 6.46786 11.8986L2.04879 7.1454H15.4005C15.7318 7.1454 16 6.85608 16 6.49983Z"
                        fill="#0038AE"
                      />
                    </svg>
                  </button>

                  <span>
                    {dateService
                      .convertDateStrToMoment(selectedMonth, API_DATE_FORMAT)
                      .format("MMMM")}{" "}
                    {dateService
                      .convertDateStrToMoment(selectedMonth, API_DATE_FORMAT)
                      .format("YYYY")}{" "}
                  </span>

                  <button
                    type="button"
                    onClick={moveRight}
                    className="focus:outline-none">
                    <svg
                      width="16"
                      height="13"
                      viewBox="0 0 16 13"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M5.68233e-07 6.50017C5.37088e-07 6.85643 0.268894 7.14539 0.599927 7.14539L13.9516 7.14539L9.53214 11.8986C9.29777 12.1506 9.29777 12.5591 9.53214 12.811C9.76609 13.063 10.1465 13.063 10.3805 12.811L15.8242 6.95619C16.0586 6.70413 16.0586 6.29564 15.8242 6.04381L10.3805 0.189044C10.2635 0.0627885 10.1099 -5.14929e-07 9.95631 -5.28357e-07C9.80271 -5.41784e-07 9.64912 0.0630192 9.53214 0.189044C9.29777 0.441107 9.29777 0.849593 9.53214 1.10142L13.9512 5.8546L0.599498 5.8546C0.268244 5.8546 5.99378e-07 6.14392 5.68233e-07 6.50017Z"
                        fill="#0038AE"
                      />
                    </svg>
                  </button>
                </div>
                <div>
                  {renderMonthDates(selectedMonth, values, setFieldValue)}
                </div>
              </div>

              <div className="flex gap-2 items-center max-lg:justify-center mt-10 mb-16 pl-12 max-lg:pl-0">
                <Field
                  type="checkbox"
                  name="nextAvailable"
                  id="nextAvailable"
                  checked={values.nextAvailable}
                  className="w-8 h-8 border border-primary rounded"></Field>
                <label htmlFor="nextAvailable" className="font-semibold">
                  Find next available appointment
                </label>
              </div>
              <p className="font-semibold max-lg:text-center my-10 pl-12 max-lg:pl-0">
                Selected&nbsp;:&nbsp;
                {values.nextAvailable
                  ? "Next Available Date"
                  : moment(values.date).format("MMM DD, YYYY")}
              </p>
            </div>

            <div className="bg-white py-2 max-lg:absolute w-full max-lg:bottom-0 px-8 max-lg:pl-0">
              <button
                type="submit"
                className="w-auto max-lg:w-[90%] mx-4 bg-black px-20 max-sm:px-4 py-4 my-4 text-white rounded-md font-bold disabled:bg-gray-400 disabled:cursor-not-allowed max-lg:px-16">
                Done
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ScheduleSessionPreferredDate;

const SlotsTemplate = ({ title, slots }) => {
  return (
    <div className="p-4">
      <div className="flex gap-x-2 items-center">
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.30125 13.125H13.6987C13.5525 12.2497 13.1006 11.4546 12.4234 10.8811C11.7461 10.3076 10.8874 9.99286 10 9.99286C9.11255 9.99286 8.25387 10.3076 7.57662 10.8811C6.89938 11.4546 6.44747 12.2497 6.30125 13.125ZM5 13.75C5 12.4239 5.52678 11.1521 6.46447 10.2145C7.40215 9.27678 8.67392 8.75 10 8.75C11.3261 8.75 12.5979 9.27678 13.5355 10.2145C14.4732 11.1521 15 12.4239 15 13.75C15 13.9158 14.9342 14.0747 14.8169 14.1919C14.6997 14.3092 14.5408 14.375 14.375 14.375H5.625C5.45924 14.375 5.30027 14.3092 5.18306 14.1919C5.06585 14.0747 5 13.9158 5 13.75ZM10 3.75C10.1658 3.75 10.3247 3.81585 10.4419 3.93306C10.5592 4.05027 10.625 4.20924 10.625 4.375V6.875C10.625 7.04076 10.5592 7.19973 10.4419 7.31694C10.3247 7.43415 10.1658 7.5 10 7.5C9.83424 7.5 9.67527 7.43415 9.55806 7.31694C9.44085 7.19973 9.375 7.04076 9.375 6.875V4.375C9.375 4.20924 9.44085 4.05027 9.55806 3.93306C9.67527 3.81585 9.83424 3.75 10 3.75ZM20 13.75C20 13.9158 19.9342 14.0747 19.8169 14.1919C19.6997 14.3092 19.5408 14.375 19.375 14.375H16.875C16.7092 14.375 16.5503 14.3092 16.4331 14.1919C16.3158 14.0747 16.25 13.9158 16.25 13.75C16.25 13.5842 16.3158 13.4253 16.4331 13.3081C16.5503 13.1908 16.7092 13.125 16.875 13.125H19.375C19.5408 13.125 19.6997 13.1908 19.8169 13.3081C19.9342 13.4253 20 13.5842 20 13.75ZM3.75 13.75C3.75 13.9158 3.68415 14.0747 3.56694 14.1919C3.44973 14.3092 3.29076 14.375 3.125 14.375H0.625C0.45924 14.375 0.300269 14.3092 0.183058 14.1919C0.065848 14.0747 0 13.9158 0 13.75C0 13.5842 0.065848 13.4253 0.183058 13.3081C0.300269 13.1908 0.45924 13.125 0.625 13.125H3.125C3.29076 13.125 3.44973 13.1908 3.56694 13.3081C3.68415 13.4253 3.75 13.5842 3.75 13.75ZM17.0712 6.67875C17.1884 6.79595 17.2542 6.9549 17.2542 7.12062C17.2542 7.28635 17.1884 7.4453 17.0712 7.5625L15.3038 9.33C15.2461 9.38969 15.1771 9.43731 15.1009 9.47006C15.0246 9.50282 14.9426 9.52006 14.8596 9.52078C14.7766 9.5215 14.6943 9.50569 14.6175 9.47426C14.5407 9.44284 14.4709 9.39643 14.4123 9.33775C14.3536 9.27906 14.3072 9.20928 14.2757 9.13247C14.2443 9.05566 14.2285 8.97336 14.2292 8.89037C14.2299 8.80739 14.2472 8.72537 14.2799 8.64912C14.3127 8.57287 14.3603 8.5039 14.42 8.44625L16.1875 6.67875C16.3047 6.56158 16.4636 6.49576 16.6294 6.49576C16.7951 6.49576 16.954 6.56158 17.0712 6.67875ZM5.58 9.33C5.4628 9.44717 5.30385 9.51299 5.13812 9.51299C4.9724 9.51299 4.81345 9.44717 4.69625 9.33L2.92875 7.5625C2.8149 7.44462 2.7519 7.28675 2.75333 7.12287C2.75475 6.959 2.82048 6.80224 2.93636 6.68636C3.05224 6.57048 3.209 6.50475 3.37287 6.50333C3.53675 6.5019 3.69462 6.5649 3.8125 6.67875L5.58 8.44625C5.69717 8.56345 5.76299 8.7224 5.76299 8.88812C5.76299 9.05385 5.69717 9.2128 5.58 9.33Z"
            fill="black"
          />
        </svg>

        <h1>{title}</h1>
      </div>
      <div className="grid grid-cols-3 gap-4 my-4 mb-4 md:grid-cols-3">
        {slots}
      </div>
    </div>
  );
};
